"use client";

import Image from "next/image";
import {
  Download,
  Share2,
  Smartphone,
  X,
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useState,
} from "react";

import type { PublicCompany } from "@/types/store";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
};

type FoodInstallPromptProps = {
  company: PublicCompany;
};

const DISMISS_PREFIX =
  "fornecedor_food_pwa_dismissed_";

const DISMISS_DURATION_MS =
  7 * 24 * 60 * 60 * 1000;

function isIosDevice() {
  if (typeof window === "undefined") {
    return false;
  }

  return (
    /iphone|ipad|ipod/i.test(
      window.navigator.userAgent,
    ) ||
    (
      navigator.platform === "MacIntel" &&
      navigator.maxTouchPoints > 1
    )
  );
}

function isStandaloneMode() {
  if (typeof window === "undefined") {
    return false;
  }

  return (
    window.matchMedia(
      "(display-mode: standalone)",
    ).matches ||
    Boolean(
      (
        window.navigator as Navigator & {
          standalone?: boolean;
        }
      ).standalone,
    )
  );
}

function dismissedRecently(slug: string) {
  try {
    const value = window.localStorage.getItem(
      `${DISMISS_PREFIX}${slug}`,
    );

    if (!value) {
      return false;
    }

    const timestamp = Number(value);

    if (!Number.isFinite(timestamp)) {
      return false;
    }

    return (
      Date.now() - timestamp <
      DISMISS_DURATION_MS
    );
  } catch {
    return false;
  }
}

export default function FoodInstallPrompt({
  company,
}: FoodInstallPromptProps) {
  const [
    installEvent,
    setInstallEvent,
  ] = useState<BeforeInstallPromptEvent | null>(
    null,
  );

  const [visible, setVisible] =
    useState(false);

  const [showIosHelp, setShowIosHelp] =
    useState(false);

  const isIos = useMemo(
    () => isIosDevice(),
    [],
  );

  useEffect(() => {
    if (company.business_type !== "food") {
      return;
    }

    /*
     * Troca o manifesto global do Fornecedor.net
     * pelo manifesto específico desta empresa.
     */
    const manifestHref =
      `/${encodeURIComponent(company.slug)}` +
      "/manifest.webmanifest";

    let manifestLink =
      document.querySelector<HTMLLinkElement>(
        'link[rel="manifest"]',
      );

    if (!manifestLink) {
      manifestLink =
        document.createElement("link");

      manifestLink.rel = "manifest";
      document.head.appendChild(manifestLink);
    }

    manifestLink.href = manifestHref;

    /*
     * No iPhone/iPad o ícone usado ao adicionar
     * à tela inicial vem do apple-touch-icon.
     */
    if (company.logo_url) {
      let appleIcon =
        document.querySelector<HTMLLinkElement>(
          'link[rel="apple-touch-icon"]',
        );

      if (!appleIcon) {
        appleIcon =
          document.createElement("link");

        appleIcon.rel =
          "apple-touch-icon";

        document.head.appendChild(
          appleIcon,
        );
      }

      appleIcon.href = company.logo_url;
    }

    document.title = company.name;

    if (
      isStandaloneMode() ||
      dismissedRecently(company.slug)
    ) {
      return;
    }

    /*
     * iOS não fornece beforeinstallprompt.
     * Podemos mostrar diretamente nossa orientação.
     */
    if (isIosDevice()) {
      const timeout =
        window.setTimeout(
          () => setVisible(true),
          1200,
        );

      return () =>
        window.clearTimeout(timeout);
    }

    const handleBeforeInstallPrompt = (
      event: Event,
    ) => {
      event.preventDefault();

      setInstallEvent(
        event as BeforeInstallPromptEvent,
      );

      window.setTimeout(
        () => setVisible(true),
        700,
      );
    };

    const handleInstalled = () => {
      setVisible(false);
      setInstallEvent(null);
    };

    window.addEventListener(
      "beforeinstallprompt",
      handleBeforeInstallPrompt,
    );

    window.addEventListener(
      "appinstalled",
      handleInstalled,
    );

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );

      window.removeEventListener(
        "appinstalled",
        handleInstalled,
      );
    };
  }, [
    company.business_type,
    company.logo_url,
    company.name,
    company.slug,
  ]);

  if (
    company.business_type !== "food" ||
    !visible ||
    isStandaloneMode()
  ) {
    return null;
  }

  const dismiss = () => {
    try {
      window.localStorage.setItem(
        `${DISMISS_PREFIX}${company.slug}`,
        String(Date.now()),
      );
    } catch {
      // localStorage indisponível: apenas fecha.
    }

    setVisible(false);
    setShowIosHelp(false);
  };

  const install = async () => {
    if (isIos) {
      setShowIosHelp(true);
      return;
    }

    if (!installEvent) {
      return;
    }

    await installEvent.prompt();

    const choice =
      await installEvent.userChoice;

    if (choice.outcome === "accepted") {
      setVisible(false);
    }

    setInstallEvent(null);
  };

  return (
    <div
      className="
        fixed inset-x-3 bottom-4 z-[100]
        mx-auto max-w-md
        rounded-2xl border border-slate-200
        bg-white p-4
        shadow-[0_20px_60px_rgba(15,23,42,0.22)]
        sm:bottom-6
      "
      role="dialog"
      aria-label={`Instalar aplicativo ${company.name}`}
    >
      <button
        type="button"
        onClick={dismiss}
        className="
          absolute right-3 top-3
          rounded-full p-1.5
          text-slate-400
          transition hover:bg-slate-100
          hover:text-slate-700
        "
        aria-label="Fechar"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex gap-3 pr-7">
        <div
          className="
            flex h-14 w-14 shrink-0
            items-center justify-center
            overflow-hidden rounded-xl
            border border-slate-200
            bg-slate-50
          "
        >
          {company.logo_url ? (
            <Image
              src={company.logo_url}
              alt={company.name}
              width={56}
              height={56}
              className="
                h-full w-full object-contain
              "
              unoptimized
            />
          ) : (
            <Smartphone
              className="
                h-7 w-7 text-slate-500
              "
            />
          )}
        </div>

        <div className="min-w-0">
          <p
            className="
              pr-2 text-sm font-semibold
              text-slate-900
            "
          >
            {company.name} no seu celular
          </p>

          <p
            className="
              mt-1 text-sm leading-5
              text-slate-600
            "
          >
            Instale nosso app para fazer
            seus pedidos mais rápido.
          </p>
        </div>
      </div>

      {showIosHelp ? (
        <div
          className="
            mt-4 rounded-xl bg-slate-50
            p-3 text-sm text-slate-700
          "
        >
          <div
            className="
              flex items-start gap-2
            "
          >
            <Share2
              className="
                mt-0.5 h-4 w-4
                shrink-0
              "
            />

            <p>
              No Safari, toque em{" "}
              <strong>Compartilhar</strong>{" "}
              e depois em{" "}
              <strong>
                Adicionar à Tela de Início
              </strong>
              .
            </p>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={install}
          disabled={!isIos && !installEvent}
          className="
            mt-4 flex w-full
            items-center justify-center
            gap-2 rounded-xl
            bg-slate-900 px-4 py-3
            text-sm font-semibold text-white
            transition hover:bg-slate-800
            disabled:cursor-not-allowed
            disabled:opacity-50
          "
        >
          <Download className="h-4 w-4" />

          Instalar aplicativo
        </button>
      )}
    </div>
  );
}
